import { useEffect, useRef, type ClipboardEvent, type KeyboardEvent } from 'react';
import { cn } from '@/lib/cn';

const NOT_ALLOWED = /[^a-z0-9]/g;

/** Normaliza a minúscula y descarta todo lo que no sea alfanumérico. */
function sanitize(raw: string, max: number): string {
  return raw.toLowerCase().replace(NOT_ALLOWED, '').slice(0, max);
}

interface CodeInputProps {
  length: number;
  value: string;
  invalid: boolean;
  disabled: boolean;
  describedBy?: string;
  onChange: (value: string) => void;
}

/**
 * Campo de código segmentado: una casilla por carácter, con el
 * comportamiento que la gente ya espera de un campo así — la escritura
 * avanza sola, backspace vuelve, las flechas navegan y pegar el código
 * completo lo reparte entre las casillas.
 *
 * El valor real es un único string; las casillas son su vista. Se muestra en
 * mayúscula por legibilidad, pero viaja en minúscula (el backend compara sin
 * distinguir capitalización).
 */
export function CodeInput({
  length,
  value,
  invalid,
  disabled,
  describedBy,
  onChange,
}: CodeInputProps) {
  const boxes = useRef<Array<HTMLInputElement | null>>([]);

  // El valor recién escrito, disponible antes de que React vuelva a renderizar.
  // Sin esto el guard de `onFocus` leería el valor viejo y devolvería el foco a
  // la casilla anterior en cuanto la escritura intenta avanzar.
  const latest = useRef(value);
  latest.current = value;

  const focusAt = (index: number): void => {
    boxes.current[Math.min(Math.max(index, 0), length - 1)]?.focus();
  };

  // Sin casillas escritas no hay dónde estar más que en la primera. Cubre los
  // dos momentos en que eso pasa: al abrir la pantalla y después de un código
  // rechazado, cuando deshabilitar el campo durante el envío perdió el foco.
  const empty = value.length === 0;
  useEffect(() => {
    if (empty) boxes.current[0]?.focus();
  }, [empty]);

  const commit = (next: string): void => {
    latest.current = next;
    onChange(next);
  };

  const handleChange = (index: number, raw: string): void => {
    const typed = sanitize(raw, 1);
    if (!typed) return;

    commit(sanitize(value.slice(0, index) + typed + value.slice(index + 1), length));
    focusAt(index + 1);
  };

  const handleKeyDown = (index: number, event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      event.preventDefault();
      focusAt(event.key === 'ArrowLeft' ? index - 1 : index + 1);
      return;
    }

    if (event.key !== 'Backspace') return;

    // Casilla vacía: borra la anterior, que es lo que el gesto quiso decir.
    event.preventDefault();
    const target = value[index] ? index : index - 1;
    if (target < 0) return;

    commit(value.slice(0, target) + value.slice(target + 1));
    focusAt(target);
  };

  const handlePaste = (event: ClipboardEvent<HTMLInputElement>): void => {
    event.preventDefault();
    const pasted = sanitize(event.clipboardData.getData('text'), length);
    if (!pasted) return;

    commit(pasted);
    focusAt(pasted.length);
  };

  return (
    <div
      role="group"
      aria-label="Código de acceso"
      aria-describedby={describedBy}
      className="flex justify-center gap-2 sm:gap-3"
    >
      {Array.from({ length }, (_, index) => (
        <input
          key={index}
          ref={(element) => {
            boxes.current[index] = element;
          }}
          type="text"
          inputMode="text"
          autoComplete={index === 0 ? 'one-time-code' : 'off'}
          autoCapitalize="off"
          autoCorrect="off"
          spellCheck={false}
          maxLength={1}
          disabled={disabled}
          aria-label={`Carácter ${index + 1} de ${length}`}
          aria-invalid={invalid}
          value={value[index] ?? ''}
          onChange={(event) => handleChange(index, event.target.value)}
          onKeyDown={(event) => handleKeyDown(index, event)}
          onPaste={handlePaste}
          onFocus={(event) => {
            // Impide saltar a una casilla adelantada y dejar huecos en el medio.
            if (index > latest.current.length) {
              focusAt(latest.current.length);
              return;
            }
            // Con maxLength=1 el navegador ignora lo que se escriba sobre una
            // casilla ya ocupada. Seleccionarla al enfocar hace que escribir
            // encima reemplace, que es lo que el gesto pretendía.
            event.target.select();
          }}
          className={cn(
            'focus-ring h-16 w-12 rounded border bg-surface-sunken text-center font-mono',
            'text-2xl font-medium uppercase text-ink caret-accent transition-colors',
            'disabled:opacity-60 sm:h-20 sm:w-16 sm:text-3xl',
            invalid ? 'border-feedback-danger-fg' : 'border-line hover:border-line_strong',
          )}
        />
      ))}
    </div>
  );
}
